import { Form, Radio, message } from "antd";
import BaseModal from "components/BaseModal";
import { MODAL_SIZE_MEDIUM } from "constants/Layout";
import _ from "lodash";
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import {
  FieldTypes,
  generateFormFields,
  nameRegexValidator,
  urlRegexValidator,
} from "utils/formUtils";
import ModalUtil from "utils/modalUtil";
import JiraMappingForm from "../jira/JiraMappingForm";
import { duplicateNameValidator } from "utils/validatorUtil";

function RallyLoginConfigForm({ name, loginConfigList, ...props }, ref) {
  const isEdit = !_.isUndefined(props.config);
  const [form] = Form.useForm();
  const [auth, setAuth] = useState(() => props.config?.tokenType ?? "default");
  const thisRef = useRef();

  useImperativeHandle(ref, () => ({
    ...thisRef,
  }));


  const onOk = async () => {
    await form.validateFields();
    try {
      const toolName = name.toUpperCase().replace(" ", "_");
      let payload = {...form.getFieldsValue(), ...(auth === "API_KEY" && {tokenType: "API_KEY"})};
      const response = await ToolsIntegrationsAPI[
        !isEdit
          ? "postTicketSystemToolLoginDetails"
          : "updateTicketSystemToolLoginDetails"
      ](toolName, payload, props.config?.id).promise;

      message.success(`${name} Login Configuration created`);
      if (!isEdit) {
        ModalUtil.show({
          content: (
            <JiraMappingForm
              onUpdateConfigComponent={props.onUpdateConfigComponent}
              name={name}
              loginConfigId={response.id}
            />
          ),
        });
      }
      props.onUpdateConfigComponent && props.onUpdateConfigComponent(true);
    } catch (err) {
      console.log(err);
      if (!_.isEmpty(err.response.data.errors)) {
        const { field, defaultMessage } = err.response?.data?.errors[0];
        form.setFields([
          {
            name: field,
            errors: [defaultMessage],
          },
        ]);
      }
      return Promise.reject(err.response?.data?.message);
    }
  };

  const authRadio = useMemo(() => (
      <Radio.Group onChange={(e) => setAuth(e.target.value)} value={auth} className="m-b-l">
        <Radio value={"default"}>Basic Authentication</Radio>
        <Radio value={"API_KEY"}>API Key Based Authentication</Radio>
      </Radio.Group>
    ), [auth]);

  const rallyFields = useMemo(() => {
    const fields = [
      {
        type: "Label",
        value: authRadio,
      },
      {
        name: "name",
        label: "Name",
        colSpan: 12,
        props: {
          autoFocus: true,
        },
        rules: [
          {
            required: true,
            message: "Name is required",
          },
          duplicateNameValidator({ list: loginConfigList }),
          nameRegexValidator,
        ],
      },
      {
        name: "url",
        label: "URL",
        colSpan: 12,
        required: true,
        rules: [urlRegexValidator()],
      },
      {
        name: "userName",
        label: "User Name",
        colSpan: 12,
        required: true,
      },
      {
        name: "apiToken",
        label: auth === "default" ? "Password" : "API Key",
        type: FieldTypes.PASSWORD,
        colSpan: 12,
        required: true,
      },
      {
        name: "version",
        label: "Version",
        colSpan: 12,
        type: FieldTypes.SELECT,
        disabled: true,
        props: {defaultValue: "v2"}
      },
    ];

    return fields;
  }, [auth, authRadio, loginConfigList]);

  const modalProps = {
    title: (
      <>
        {isEdit ? "Edit" : "Add"} {name} Login Credentials{" "}
      </>
    ),
    width: MODAL_SIZE_MEDIUM,
    onOk: onOk,
  };

  return (
    <BaseModal
      modalProps={modalProps}
      ref={thisRef}
      afterClose={props.afterClose}
    >
      <Form
        initialValues={{...props.config, version: "v2", url: "https://rally1.rallydev.com"}}
        form={form}
        layout="vertical"
      >
        {generateFormFields(rallyFields)}
      </Form>
    </BaseModal>
  );
}

export default forwardRef(RallyLoginConfigForm);
